import { type JSX,useCallback } from 'react';

import { Box, Stack } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES, ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ClaimValueField } from 'common/components/sharedCustomComponents/Claims/ClaimValueField';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { useClaimMembershipDrawer } from 'common/hooks/useClaimMembershipDrawer';
import {
    APIUserClaimMembershipPostParams,
    APIUserClaimMembershipPutParams
} from 'common/services/api/UserClaimMembership/UserClaimMembership.api';
import {
    postUserClaimMembership,
    PostUserClaimThunkResponse,
    putUserClaimMembership,
    PutUserClaimThunkResponse
} from 'common/services/api/UserClaimMembership/UserClaimMembership.thunk';
import { Claim, UserClaimMembership } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { CLAIM_VALUE_NOT_CHANGED_ERROR_MESSAGE } from 'common/utils/claimErrorHandlers';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { ClaimNameDropDown } from 'features/roleClaimMembership/ClaimNameDropdown';

interface UserClaimItem {
    name: string;
}

interface UserClaimDrawerProperties {
    tenantName: string;
    tenantId: string;
    userId: string;
    userFirstName: string;
    userLastName: string;
    userFullName: string;
}

function useUserClaimDrawer(
    entityName: string,
    drawerProps: UserClaimDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<UserClaimMembership & UserClaimItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();

    const drawerContext = useDrawerContext<
        UserClaimMembership & UserClaimItem,
        UserClaimDrawerProperties,
        APIUserClaimMembershipPostParams,
        PostUserClaimThunkResponse,
        APIUserClaimMembershipPutParams,
        PutUserClaimThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const claimMembership = useClaimMembershipDrawer({
        drawerId: DrawerIDs.Claims,
        currentId: drawerContext.currentId,
        isOpenDrawer: drawerContext.isOpenDrawer,
        action: drawerContext.action,
        editingClaimValue: editingItemInfo?.editingRow?.value,
        entityType: 'Claim'
    });

    const getPostOptions = useCallback((): APIUserClaimMembershipPostParams => ({
        tenantId: drawerProps.tenantId,
        userId: drawerProps.userId,
        claimId: claimMembership.claim.id,
        value: claimMembership.claimValue || '',
    }), [drawerProps.tenantId, drawerProps.userId, claimMembership.claim.id, claimMembership.claimValue]);

    const getPutOptions = useCallback((): APIUserClaimMembershipPutParams => {
        claimMembership.setValueInConflict(false);
        return {
            tenantId: drawerProps.tenantId,
            userId: drawerProps.userId,
            claimId: editingItemInfo?.editingRow?.claimId ?? '',
            value: claimMembership.claimValue,
        };
    }, [drawerProps.tenantId, drawerProps.userId, editingItemInfo?.editingRow?.claimId, claimMembership.claimValue, claimMembership.setValueInConflict]);

    const createSuccessHandler = useCallback((postResult: PostUserClaimThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setRows(postResult.rows));
    }, [drawerContext, dispatch]);

    const editSuccessHandler = useCallback((putResult: PutUserClaimThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postUserClaimMembership, getPostOptions, createSuccessHandler, claimMembership.createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, claimMembership.createFailureHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putUserClaimMembership, getPutOptions, editSuccessHandler, claimMembership.editFailureHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler, claimMembership.editFailureHandler]);

    return {
        ...drawerContext,
        claim: claimMembership.claim,
        claimValue: claimMembership.claimValue,
        valueInConflict: claimMembership.valueInConflict,
        handleClaimChange: claimMembership.handleClaimChange,
        handleClaimValueChange: claimMembership.handleClaimValueChange,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    claim,
    claimValue,
    valueInConflict,
    handleClaimChange,
    handleClaimValueChange,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    claim: Claim;
    claimValue: string;
    valueInConflict: boolean;
    handleClaimChange: (_e: unknown, newClaim: Claim) => void;
    handleClaimValueChange: (value: string) => void;
    postCallback: () => void;
    drawerProps: UserClaimDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ClaimNameDropDown
                    value={claim}
                    onChange={handleClaimChange}
                    tenantId={drawerProps.tenantId}
                    hasError={nameInConflict}
                />
                {nameInConflict &&
                    <ErrorHandler
                        errorMessage={'This Claim is already linked to this User.'}
                    />
                }
                <ReadOnlyTextField label="Name" value={drawerProps.userFullName} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
                <ClaimValueField
                    claim={claim}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                    hasError={valueInConflict}
                />
                {valueInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        The Claim value was not changed
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    claimValue,
    valueInConflict,
    handleClaimValueChange,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    claimValue: string;
    valueInConflict: boolean;
    handleClaimValueChange: (value: string) => void;
    putCallback: () => void;
    drawerProps: UserClaimDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<UserClaimMembership & UserClaimItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ReadOnlyTextField label="Claim Name" value={editingItemInfo?.editingRow?.claimName ?? ''} />
                <ReadOnlyTextField label="Name" value={drawerProps.userFullName} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
                <ClaimValueField
                    claim={editingItemInfo?.editingRow}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                    hasError={valueInConflict}
                />
                {valueInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        {CLAIM_VALUE_NOT_CHANGED_ERROR_MESSAGE}
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

export function UserClaimMembershipCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<UserClaimMembership & UserClaimItem>>) {
    const pageInfo = useAppSelector(selectPageInfo);
    const userFirstName = pageInfo?.firstName ? pageInfo.firstName : 'Unknown';
    const userLastName = pageInfo?.lastName ? pageInfo.lastName : 'User';
    const userClaimDrawerProps: UserClaimDrawerProperties = {
        tenantId: pageInfo?.id ?? '',
        tenantName: pageInfo?.name ?? '',
        userId: pageInfo?.userId ?? '',
        userFirstName,
        userLastName,
        userFullName: `${userFirstName} ${userLastName}`
    };

    const drawerState = useUserClaimDrawer(title, userClaimDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                claim={drawerState.claim}
                claimValue={drawerState.claimValue}
                valueInConflict={drawerState.valueInConflict}
                handleClaimChange={drawerState.handleClaimChange}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                postCallback={drawerState.postCallback}
                drawerProps={userClaimDrawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                claimValue={drawerState.claimValue}
                valueInConflict={drawerState.valueInConflict}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                putCallback={drawerState.putCallback}
                drawerProps={userClaimDrawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
